import React, { useEffect, useState } from "react";
import { Loader2, ShoppingBag, Check, X as XIcon } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const ShopPanel = ({ token }) => {
  const a = adminApi(token);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try { setItems(await a.listShop() || []); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleAvail = async (it) => {
    setSavingId(it.id);
    try {
      const r = await a.updateShopItem(it.id, { available: !it.available });
      setItems((prev) => prev.map((x) => x.id === it.id ? r : x));
      toast({ title: r.available ? `${r.name} ist jetzt im Shop` : `${r.name} ausgeblendet` });
    } catch {
      toast({ title: "Fehler", variant: "destructive" });
    } finally { setSavingId(null); }
  };

  const updateCost = async (it, newCost) => {
    if (newCost === it.cost) return;
    setSavingId(it.id);
    try {
      const r = await a.updateShopItem(it.id, { cost: newCost });
      setItems((prev) => prev.map((x) => x.id === it.id ? r : x));
    } finally { setSavingId(null); }
  };

  const byCat = items.reduce((acc, it) => {
    const k = it.category || "Stil";
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-shop-panel">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-amber-300" /> Punkte-Shop verwalten</h2>
        <p className="mt-1 text-xs text-zinc-400">Gib Glows für den Shop frei. Alle Items haben einen voreingestellten Preis, den du anpassen kannst.</p>
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-zinc-500">
          <span>{items.filter((i) => i.available).length} / {items.length} aktiv</span>
        </div>
      </div>

      {Object.entries(byCat).map(([cat, list]) => (
        <div key={cat} className="rounded-xl border border-white/10 bg-[#0f141b] p-4">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#9d7bff]">{cat} · {list.length} Items</div>
          <div className="space-y-2">
            {list.map((it) => (
              <div
                key={it.id}
                data-testid={`admin-shop-row-${it.slug}`}
                className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${it.available ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/10 bg-[#10141b]"}`}
              >
                <div
                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
                  style={it.gradient ? { background: it.gradient, boxShadow: `0 0 18px ${it.color}` } : { background: it.color, boxShadow: `0 0 18px ${it.color}` }}
                  title={it.icon}
                >
                  <span className="text-base">{it.icon}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white">{it.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{it.season || it.category} · {it.slug}</div>
                  {it.description && <div className="mt-0.5 line-clamp-1 text-[11px] text-zinc-400">{it.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    defaultValue={it.cost}
                    onBlur={(e) => updateCost(it, parseInt(e.target.value, 10) || 0)}
                    className="h-8 w-24 bg-[#0f141b] border-white/10 text-zinc-200 text-sm"
                    data-testid={`admin-shop-cost-${it.slug}`}
                  />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</span>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <Switch
                    data-testid={`admin-shop-toggle-${it.slug}`}
                    checked={it.available}
                    disabled={savingId === it.id}
                    onCheckedChange={() => toggleAvail(it)}
                  />
                  {it.available ? (
                    <span className="font-semibold text-emerald-300"><Check className="mr-1 inline h-3 w-3" />Im Shop</span>
                  ) : (
                    <span className="text-zinc-500"><XIcon className="mr-1 inline h-3 w-3" />Versteckt</span>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShopPanel;
